import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../reusable/PageHeader';
import Panel from '../reusable/Panel';
import CustomButton from '../reusable/CustomButton';
import SelectDropdown from '../reusable/SelectDropdown';
import Loader from '../reusable/Loader';
import AudiencePanel from './AudiencePanel';
import ResponseRateLegend from './ResponseRateLegend';
import {
  getSurveyResponseStats,
  getSurveyResponseStatsPublic,
  SurveyResponseStatsResponse,
} from '../../services/surveyService';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './SurveyResponseDashboardPage.css';

const ALL_SCHOOLS_VALUE = 'All schools';

interface SurveyResponseDashboardContentProps {
  eoaId: string;
  accessToken?: string;
  onError?: (message: string, error: Error) => void;
}

const SurveyResponseDashboardContent = ({ eoaId, accessToken, onError }: SurveyResponseDashboardContentProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stats, setStats] = useState<SurveyResponseStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const initialSchoolId = searchParams.get('schoolId') ?? ALL_SCHOOLS_VALUE;
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>(initialSchoolId);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const data = accessToken
        ? await getSurveyResponseStats(eoaId, accessToken)
        : await getSurveyResponseStatsPublic(eoaId);
      setStats(data);
    } catch (error) {
      const err = error as Error;
      if (onError) {
        onError('Error fetching survey response statistics', err);
      } else {
        const status = (error as any)?.response?.status;
        setFetchError(status === 404 ? '404 — Page not found.' : 'Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [eoaId, accessToken, onError]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const schoolItems = useMemo(() => {
    if (!stats) return [{ label: 'All schools', value: ALL_SCHOOLS_VALUE }];
    const map = new Map<string, string>();
    [stats.students, stats.families, stats.staff].forEach((audience) =>
      audience.per_school.forEach((s) => map.set(s.school_id, s.school_name))
    );
    return [
      { label: 'All schools', value: ALL_SCHOOLS_VALUE },
      ...Array.from(map.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [stats]);

  const handleSchoolChange = (event: any) => {
    const value = event.target.value as string;
    setSelectedSchoolId(value);
    if (value && value !== ALL_SCHOOLS_VALUE) {
      setSearchParams({ schoolId: value });
    } else {
      setSearchParams({});
    }
  };

  const resolvedSchoolId = selectedSchoolId === ALL_SCHOOLS_VALUE ? null : selectedSchoolId;

  if (loading) return <Loader />;

  if (fetchError) return <p className="center-text">{fetchError}</p>;

  return (
    <>
      <PageHeader title="Survey response dashboard" />
      <div className="survey-response-school-selector-bar">
        <span className="survey-response-school-selector-label">Display data for</span>
        <SelectDropdown
          label="School"
          selectedValue={selectedSchoolId}
          handleChange={handleSchoolChange}
          items={schoolItems}
          isClearable={false}
          size="small"
          fullWidth={false}
        />
      </div>
      <div className="main-content survey-response-dashboard">
        <CustomButton
          variant="text"
          buttonText="Back"
          icon={<img src={GoBackIcon} alt="go back" />}
          iconPosition="start"
          buttonAction={() => navigate(-1)}
        />

        <Panel className="survey-response-legend-panel">
          <h2 className="survey-response-legend-title">Survey participation key</h2>
          <ResponseRateLegend />
        </Panel>

        {stats && (
          <div className="survey-response-audience-panels">
            <AudiencePanel
              title="Students"
              audienceStats={stats.students}
              selectedSchoolId={resolvedSchoolId}
              countLabel="students"
              responseRateLabel="Student Response Rate"
            />
            <AudiencePanel
              title="Families"
              audienceStats={stats.families}
              selectedSchoolId={resolvedSchoolId}
              countLabel="families"
              responseRateLabel="Family Response Rate"
            />
            <AudiencePanel
              title="Staff"
              audienceStats={stats.staff}
              selectedSchoolId={resolvedSchoolId}
              countLabel="staff"
              responseRateLabel="Staff Response Rate"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SurveyResponseDashboardContent;
